import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, Loader2, MapPin, Calendar, Building2, User,
  ClipboardList, AlertTriangle, ThumbsUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_STYLE = {
  Low: 'bg-slate-500/20 text-slate-200 border-slate-400/30',
  Med: 'bg-yellow-500/20 text-yellow-200 border-yellow-400/30',
  High: 'bg-red-500/20 text-red-200 border-red-400/30',
  Urgent: 'bg-red-600/40 text-red-50 border-red-400/60',
};

// Survives StrictMode's double-mount so an emailed ?action= link only POSTs once
const autoFired = new Set();

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-white break-words">{value || '—'}</div>
    </div>
  </div>
);

/** Public page an assignee lands on from the one-tap link in their email. */
export default function WorkOrderRespond() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const presetAction = params.get('action');

  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/work-orders/respond/${token}`);
      setWo(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'This link is no longer valid.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = useCallback(async (action) => {
    setBusy(action);
    try {
      const { data } = await axios.post(`${API}/work-orders/respond/${token}`, { action, note: note || null });
      setDone(data);
      setWo(data.work_order);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not record your response.');
    } finally {
      setBusy('');
    }
  }, [token, note]);

  // Honour ?action=accept / ?action=complete straight from the email button (fires once)
  useEffect(() => {
    const key = `${token}:${presetAction}`;
    if (wo && !done && ['accept', 'complete', 'decline'].includes(presetAction) && !autoFired.has(key)) {
      autoFired.add(key);
      respond(presetAction);
    }
  }, [wo, done, presetAction, token, respond]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1120] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error && !wo) {
    return (
      <div className="min-h-screen bg-[#0b1120] flex items-center justify-center px-6">
        <div className="text-center max-w-sm" data-testid="wo-respond-error">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h1 className="text-white text-lg font-semibold">Link not valid</h1>
          <p className="text-slate-400 text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const already = wo?.assignee?.response;

  return (
    <div className="min-h-screen bg-[#0b1120] px-4 py-6 sm:py-10" data-testid="wo-respond-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        <div className="flex items-center gap-2 mb-5">
          <ClipboardList className="w-5 h-5 text-cyan-400" />
          <span className="text-[11px] uppercase tracking-[2px] text-slate-500">MedexOVA · M.O.A. Work Order</span>
        </div>

        {done && (
          <div
            className="mb-5 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-4 flex items-start gap-3"
            data-testid="wo-respond-confirmation"
          >
            <ThumbsUp className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-white font-semibold">You {done.action} this work order</div>
              <div className="text-sm text-emerald-100/80 mt-0.5">
                Status is now {String(done.status).replace('_', ' ')} · the team has been notified.
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10">
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono text-xs text-cyan-300">{wo.wo_number}</div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs border ${PRIORITY_STYLE[wo.priority] || ''}`}>{wo.priority}</span>
                <span className="px-2 py-1 rounded text-xs border border-white/15 text-slate-300 capitalize">
                  {String(wo.status || '').replace('_', ' ')}
                </span>
              </div>
            </div>
            <h1 className="text-xl font-bold text-white mt-2">{wo.business_client}</h1>
            <p className="text-sm text-slate-400 mt-1">Hi {wo.assignee?.name} — this one's assigned to you.</p>
          </div>

          <div className="px-5 py-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">What needs doing</div>
            <p className="text-sm text-white whitespace-pre-wrap">{wo.work_description}</p>
            {wo.instructions && (
              <>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-4 mb-1">Instructions</div>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{wo.instructions}</p>
              </>
            )}
          </div>

          {(wo.attachments || []).length > 0 && (
            <div className="px-5 pb-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Photos</div>
              <div className="grid grid-cols-3 gap-2">
                {wo.attachments.map((a) => (
                  <a key={a.id} href={`${BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer">
                    <img
                      src={`${BACKEND_URL}${a.url}`}
                      alt={a.filename}
                      className="w-full aspect-square object-cover rounded-lg border border-white/10"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="px-5 pb-4">
            <Row icon={Calendar} label="Due" value={wo.date_due} />
            <Row icon={MapPin} label="Location" value={wo.location_for_service} />
            <Row icon={Building2} label="Departments" value={(wo.departments_needed || []).join(', ')} />
            <Row icon={User} label="Requested by" value={`${wo.requested_by} (${wo.requested_by_department})`} />
          </div>
        </div>

        {!done && (
          <div className="mt-5 space-y-3">
            {already && (
              <p className="text-xs text-amber-200/80 text-center">
                You already {already} this — responding again will update the team.
              </p>
            )}
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note back to the team…"
              className="w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 px-4 py-3 text-sm"
              data-testid="wo-respond-note"
            />
            <button
              onClick={() => respond('accept')}
              disabled={!!busy}
              className="w-full min-h-[52px] rounded-xl bg-cyan-500 text-black font-bold text-base disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="wo-respond-accept"
            >
              {busy === 'accept' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Accept This Job
            </button>
            <button
              onClick={() => respond('complete')}
              disabled={!!busy}
              className="w-full min-h-[52px] rounded-xl bg-emerald-500 text-black font-bold text-base disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="wo-respond-complete"
            >
              {busy === 'complete' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Mark It Done
            </button>
            <button
              onClick={() => respond('decline')}
              disabled={!!busy}
              className="w-full min-h-[48px] rounded-xl bg-white/5 border border-white/15 text-slate-300 font-medium disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="wo-respond-decline"
            >
              {busy === 'decline' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Can't take this
            </button>
          </div>
        )}

        {error && wo && (
          <p className="text-xs text-red-300 text-center mt-4" data-testid="wo-respond-inline-error">{error}</p>
        )}

        <p className="text-[11px] text-slate-600 text-center mt-8">
          Internal M.O.A. work order · MedexOVA Ops Suite
        </p>
      </motion.div>
    </div>
  );
}
